import {
  CaseReadFlagsEnum,
  LocationReservationRead,
  ReservationRead,
  ReservationRoomRead,
  BookingHotelRoomBookingRead,
  BookingMeetingRoomBookingRead,
  BookingMeetingRoomItemBookingRead,
  BookingMeetingRoomPackageDealBookingRead,
  BookingMeetingRoomHallBookingRead,
  BookingRead,
  BookingSubtotalBookingRead,
  LocationRead,
  BookingProductBookingRead,
} from '@/api';
import { addDays, format, isSameDay, parseISO } from 'date-fns';
import { cloneDeep, debounce } from 'lodash-es';
import { computed, Ref, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from '@/store';
import { v4 as uuidv4 } from 'uuid';
import {
  hasReservationPricingChanged,
  simplifyReservation,
} from '@/composables/useReservationPricing';

const reservationLoaded = ref(false);
const reservation = ref<ReservationRead>({} as ReservationRead);
const bookingSubtotals = ref<BookingSubtotalBookingRead[]>([]);
const bookingRateProducts = ref<BookingProductBookingRead[]>([]);
const totalPriceIncludingTaxes = ref<number | undefined>(undefined);
const totalPriceExcludingTaxes = ref<number | undefined>(undefined);
const pricingTaxScheme = ref<string>('VAT');
const retrievedReservation = ref<ReservationRead>({} as ReservationRead);
const watcherInitialized = ref(false);
const debouncedUpdate = ref<((...args: any[]) => void) | null>(null);
const transitionBlockerList = ref<{ [key: string]: string[] }>({});

const reservationSubmitted = ref(false);
const fetchingTransitions = ref(false);
export default function useReservationStore(): {
  reservation: Ref<ReservationRead>;
  reservationLoaded: Ref<boolean>;
  reservationChanged: Ref<boolean>;
  reservationLocation: Ref<LocationReservationRead | undefined | null>;
  missingInfo: Ref<boolean>;
  addDay: () => void;
  cloneDay: (index: number) => void;
  cloneRoom: (index: number) => void;
  deleteDay: (index: number) => void;
  setLocale: (locale: string) => void;
  getExistingReservation: () => Promise<any>;
  parseURLParams: () => Promise<any>;
  reservationLoading: Ref<boolean>;
  reservationSubmitting: Ref<boolean>;
  numRooms: Ref<number>;
  postReservation: (method: string) => Promise<any>;
  websiteURL: Ref<string>;
  breakfastSelectable: Ref<boolean>;
  breakfastDefault: Ref<boolean>;
  termsUrl: Ref<string | undefined>;
  earliestDate: Ref<Date | undefined>;
  updateBooking: () => Promise<void>;
  bookingTransitions: Ref<string[]>;
  getBooking: () => Promise<void>;
  getBookingBilling: () => Promise<void>;
  bookingId: Ref<number | undefined | null>;
  bookingSubtotals: Ref<BookingSubtotalBookingRead[]>;
  totalPriceIncludingTaxes: Ref<number | undefined>;
  totalPriceExcludingTaxes: Ref<number | undefined>;
  pricingTaxScheme: Ref<string>;
  reservationSubmitted: Ref<boolean>;
  bookingRateProducts: Ref<BookingProductBookingRead[]>;
  transitionBlockerList: Ref<{ [key: string]: string[] }>;
  fetchingTransitions: Ref<boolean>;
} {
  const store = useStore();
  // Using imported reservation pricing utilities from the composable

  // Helper function to update reservation data from booking response (includes transitions)
  const updateReservationFromBooking = (booking: BookingRead | undefined) => {
    if (!booking) {
      return;
    }

    totalPriceIncludingTaxes.value =
      booking.totalPriceIncludingTaxes ?? undefined;
    totalPriceExcludingTaxes.value =
      booking.totalPriceExcludingTaxes ?? undefined;
    pricingTaxScheme.value = booking.pricingTaxScheme ?? 'VAT';

    bookingSubtotals.value = booking.subtotals ?? [];
    bookingRateProducts.value =
      booking?.products?.filter((product) => !!product.rateProduct) ?? [];

    // Store transition blocker list (only for GET requests)
    transitionBlockerList.value = booking.transitionBlockerList ?? {};

    updateReservationRoomsAndDays(booking);
  };

  // Helper function to update reservation data from PUT booking response (excludes transitions)
  const updateReservationFromBookingPut = (
    booking: BookingRead | undefined,
  ) => {
    if (!booking) {
      return;
    }

    totalPriceIncludingTaxes.value =
      booking.totalPriceIncludingTaxes ?? undefined;
    totalPriceExcludingTaxes.value =
      booking.totalPriceExcludingTaxes ?? undefined;
    pricingTaxScheme.value = booking.pricingTaxScheme ?? 'VAT';

    bookingSubtotals.value = booking.subtotals ?? [];
    bookingRateProducts.value =
      booking?.products?.filter((product) => !!product.rateProduct) ?? [];

    // Do not update transition blocker list from PUT requests

    updateReservationRoomsAndDays(booking);
  };

  // Helper function to update rooms and days data from booking response
  const updateReservationRoomsAndDays = (booking: BookingRead) => {
    // Update hotel rooms
    (booking?.hotelRooms ?? []).forEach(
      (room: BookingHotelRoomBookingRead, index: number) => {
        const reservationRoom = reservation.value.rooms?.[index];
        if (reservationRoom) {
          if (room.id !== undefined && room.id !== null) {
            reservationRoom.id = room.id;
          }
          if (room['@id'] !== undefined && room['@id'] !== null) {
            reservationRoom['@id'] = room['@id'];
          }
          if (room.price !== undefined && room.price !== null) {
            reservationRoom.price = room.price;
          }
        }
      },
    );

    // Update meeting rooms
    (booking?.meetingRooms ?? []).forEach(
      (day: BookingMeetingRoomBookingRead, index: number) => {
        const reservationDay = reservation.value.days?.[index];
        if (reservationDay) {
          if (day.id !== undefined && day.id !== null) {
            reservationDay.id = day.id;
          }
          if (day['@id'] !== undefined && day['@id'] !== null) {
            reservationDay['@id'] = day['@id'];
          }
          // Update items
          (day.items ?? []).forEach(
            (item: BookingMeetingRoomItemBookingRead, index: number) => {
              const reservationItem = reservationDay.items?.[index];
              if (reservationItem) {
                if (item.id !== undefined && item.id !== null) {
                  reservationItem.id = item.id;
                }
                if (item['@id'] !== undefined && item['@id'] !== null) {
                  reservationItem['@id'] = item['@id'];
                }
              }
            },
          );
          // Update package deals
          (day.packageDeals ?? []).forEach(
            (
              packageDeal: BookingMeetingRoomPackageDealBookingRead,
              index: number,
            ) => {
              const reservationPackageDeal =
                reservationDay.packageDeals?.[index];
              if (reservationPackageDeal) {
                if (packageDeal.id !== undefined && packageDeal.id !== null) {
                  reservationPackageDeal.id = packageDeal.id;
                }
                if (
                  packageDeal['@id'] !== undefined &&
                  packageDeal['@id'] !== null
                ) {
                  reservationPackageDeal['@id'] = packageDeal['@id'];
                }
                if (
                  packageDeal.price !== undefined &&
                  packageDeal.price !== null
                ) {
                  reservationPackageDeal.price = packageDeal.price;
                }
              }
            },
          );
          // Update halls
          (day.halls ?? []).forEach(
            (hall: BookingMeetingRoomHallBookingRead, index: number) => {
              const reservationHall = reservationDay.halls?.[index];
              if (reservationHall) {
                if (hall.id !== undefined && hall.id !== null) {
                  reservationHall.id = hall.id;
                }
                if (hall['@id'] !== undefined && hall['@id'] !== null) {
                  reservationHall['@id'] = hall['@id'];
                }
                if (hall.price !== undefined && hall.price !== null) {
                  reservationHall.price = hall.price;
                }
              }
            },
          );
        }
      },
    );
  };

  const putBooking = async () => {
    store.commit('reservation/setReservationSubmitting', true);

    // Ensure bookingId is defined before proceeding
    if (bookingId.value === undefined || bookingId.value === null) {
      store.commit('reservation/setReservationSubmitting', false);
      return;
    }

    try {
      const booking = (await store.dispatch(
        'reservation/putBooking',
      )) as BookingRead;

      // Validate the booking response exists before proceeding
      if (!booking) {
        return;
      }

      updateReservationFromBookingPut(booking);
      store.commit('reservation/setReservationSubmitting', false);
    } catch (error: unknown) {
      // Only log error if it's not an abort error
      if (error instanceof Error && error.name !== 'AbortError') {
        console.error('Failed to put booking:', error);
        store.commit('reservation/setReservationSubmitting', false);
      }
    }
  };

  // Only initialize the watcher once
  if (!watcherInitialized.value) {
    watcherInitialized.value = true;

    // Keep a previous snapshot for comparison
    let previousReservationSnapshot = simplifyReservation(reservation.value);

    // Create new debounced function if it doesn't exist
    if (!debouncedUpdate.value) {
      debouncedUpdate.value = debounce(() => {
        putBooking();
      }, 200);
    }

    watch(
      () => reservation.value,
      (newReservation) => {
        store.commit('reservation/setReservation', newReservation);

        // Create a new snapshot for comparison using simplified data
        const currentSnapshot = cloneDeep(simplifyReservation(newReservation));

        // Only trigger update if there's an actual change
        if (
          !reservationSubmitted.value &&
          hasReservationPricingChanged(
            currentSnapshot,
            previousReservationSnapshot,
          )
        ) {
          // Call the debounced function
          if (debouncedUpdate.value) {
            debouncedUpdate.value();
          }
        }

        // Update the previous snapshot for next comparison
        previousReservationSnapshot = currentSnapshot;
      },
      { deep: true },
    );
  }

  const getExistingReservation = async (): Promise<any> => {
    const route = useRoute();
    const id = route.params.id as string;
    const username = route.params.username as string;
    const key = encodeURIComponent(route.query.key as string);

    store.commit('reservation/setConfig', {
      headers: { username: username, key: key },
      withCredentials: false,
    });
    store.commit('reservation/setReservationLoading', true);
    await store.dispatch('reservation/getReservation', { id }).then((res) => {
      reservation.value = cloneDeep(res);
      retrievedReservation.value = cloneDeep(reservation.value);
      reservationLoaded.value = true;
    });
    store.commit('reservation/setReservationLoading', false);
  };

  const route = useRoute();
  const getBooking = async (): Promise<void> => {
    // Use bookingId from store instead of route params
    const id = bookingId.value;
    if (!id) {
      console.error('Cannot get booking: bookingId is not available');
      return;
    }

    fetchingTransitions.value = true;
    try {
      const booking = await store.dispatch('reservation/getBooking', { id });
      updateReservationFromBooking(booking);
    } catch (error) {
      console.error('Failed to get booking:', error);
    } finally {
      fetchingTransitions.value = false;
    }
  };

  const getBookingBilling = async (): Promise<void> => {
    const id = route.params.id as string;
    const username = route.params.username
      ? (route.params.username as string)
      : undefined;
    const key = route.query.key
      ? encodeURIComponent(route.query.key as string)
      : undefined;

    store.commit('reservation/setReservationLoading', true);
    try {
      const booking = await store.dispatch('reservation/getBookingBilling', {
        id,
        username,
        key,
      });
      reservation.value = cloneDeep(booking);
      retrievedReservation.value = cloneDeep(reservation.value);
      reservationLoaded.value = true;
      updateReservationFromBooking(booking);
    } catch (error) {
      console.error('Failed to get booking billing:', error);
    } finally {
      store.commit('reservation/setReservationLoading', false);
    }
  };

  const parseURLParams = async () => {
    const params = route.query;

    if (params.locationID) {
      await store.state.locations.locationsPromise; // wait for locations to be loaded
    }

    reservation.value = await store.dispatch('reservation/parseRouteParams', {
      params,
      locations: store.state.locations.locations,
    });
  };

  const reservationChanged = computed(() => {
    if (!reservationLoaded.value) {
      return false;
    }
    const current = cloneDeep(reservation.value);
    const retrieved = cloneDeep(retrievedReservation.value);

    // Check if locations exist before comparing
    if (current.location?.id !== retrieved.location?.id) {
      // Location changed
      return true;
    }
    // Retrieved location does not have all information, 'current' does, even with same ID
    delete current.location;
    delete retrieved.location;

    delete current.locale;
    delete retrieved.locale;

    // Retrieved package deals formats also do not match (and are filled in by the server anyway)
    current.days?.map((day: Record<string, any>) => delete day.packageDeals);
    retrieved.days?.map((day: Record<string, any>) => delete day.packageDeals);

    return JSON.stringify(current) !== JSON.stringify(retrieved);
  });

  const reservationLoading = computed(
    () => store.state.reservation.reservationLoading,
  );

  const reservationSubmitting = computed(
    () =>
      store.state.reservation.reservationSubmitting ||
      fetchingTransitions.value,
  );

  const reservationLocation = computed(
    () =>
      reservation.value?.location ??
      store.state.reservation?.reservation?.location,
  );

  const missingInfo = computed<boolean>(
    () =>
      store.state.reservation.reservation?.flags?.includes(
        CaseReadFlagsEnum.Bricksandbasics,
      ) ?? false,
  );

  const numRooms = computed(() => {
    const blocks = [] as Array<{ startDate: string; endDate: string }>;
    if (!store.state.reservation.reservation?.rooms) {
      return 0;
    }

    store.state.reservation.reservation.rooms.forEach(
      (room: ReservationRoomRead) => {
        const block = blocks.find(
          (block) =>
            block.startDate === room.startDate &&
            block.endDate === room.endDate,
        );
        // Skip invalid room entries with missing or invalid dates
        if (!room.startDate || !room.endDate) {
          return;
        }

        // Ensure dates are valid before comparing
        try {
          new Date(room.startDate);
          new Date(room.endDate);
        } catch (e) {
          return;
        }

        if (!block) {
          blocks.push({
            startDate: room.startDate,
            endDate: room.endDate,
          });
        }
      },
    );
    return blocks.length;
  });

  const websiteURL = computed(() => {
    const website = store.state.reservation.reservation.location?.website;
    if (!website) {
      return '';
    }
    const prefix = website.startsWith('http') ? '' : 'https://';
    return prefix + website;
  });

  const hasLocationSettings = (loc: any): loc is LocationRead => {
    return loc && typeof loc?.locationSettings === 'object';
  };

  const breakfastDefault = computed(() => {
    const location = reservationLocation.value;
    const locationWithSettings = hasLocationSettings(location)
      ? location
      : undefined;
    if (!locationWithSettings) {
      return true;
    }
    return (
      locationWithSettings?.locationSettings?.breakfastEnabledByDefault ?? true
    );
  });

  const breakfastSelectable = computed(() => {
    const location = reservationLocation.value;
    const locationWithSettings = hasLocationSettings(location)
      ? location
      : undefined;

    if (!locationWithSettings) {
      return true;
    }

    return locationWithSettings?.locationSettings?.breakfastSelectable ?? true;
  });

  const termsUrl = computed(() => {
    const url = store.state.reservation.reservation?.termsUrl;
    return url ? url : undefined;
  });

  const addDay = (): void => {
    if (!reservation.value.days) {
      reservation.value.days = [];
    }
    reservation.value.days!.push({
      tempId: uuidv4(),
      date: undefined,
      halls: [],
      packageDeals: [],
      items: [],
    } as any);
  };

  const findNextDate = (start: Date): { nextDate: Date; newIndex: number } => {
    let nextPossibleDay = addDays(start, 1);
    let newIndex = -1;
    let matchingIndex =
      reservation.value.days?.findIndex((day: any) => {
        if (!day.date) {
          return false;
        }
        return isSameDay(parseISO(day.date), nextPossibleDay);
      }) ?? -1;

    while (matchingIndex !== -1) {
      nextPossibleDay = addDays(nextPossibleDay, 1);
      newIndex = matchingIndex; // Place new day after closest earlier date.
      matchingIndex =
        reservation.value.days?.findIndex((day: any) =>
          isSameDay(parseISO(day.date), nextPossibleDay),
        ) ?? -1;
    }
    return { nextDate: nextPossibleDay, newIndex };
  };

  const cloneDay = (index: number): void => {
    if (!reservation.value.days) {
      return;
    }
    const curDay = reservation.value.days[index];
    const newDay = cloneDeep(curDay);
    newDay.id = undefined;
    newDay['@id'] = undefined;
    // Generate a new unique ID for the cloned day
    (newDay as any).tempId = uuidv4();
    if (newDay.date) {
      const { nextDate, newIndex } = findNextDate(parseISO(newDay.date));
      newDay.date = format(nextDate, 'yyyy-MM-dd');
      if (newIndex !== -1) {
        index = newIndex;
      }
    }
    if (newDay.halls) {
      newDay.halls.forEach((hall: any) => {
        hall.id = undefined;
        hall['@id'] = undefined;
      });
    }
    if (newDay.packageDeals) {
      newDay.packageDeals.forEach((packageDeal: any) => {
        packageDeal.id = undefined;
        packageDeal['@id'] = undefined;
      });
    }
    if (newDay.items) {
      newDay.items.forEach((item: any) => {
        item.id = undefined;
        item['@id'] = undefined;
      });
    }
    reservation.value.days!.splice(index + 1, 0, newDay);
  };

  const cloneRoom = (index: number): void => {
    if (!reservation.value.rooms) {
      return;
    }
    const curDay = reservation.value.rooms[index];
    const newDay = cloneDeep(curDay);
    reservation.value.rooms!.splice(index + 1, 0, newDay);
  };

  const deleteDay = (index: number) => {
    reservation.value.days!.splice(index, 1);
  };

  const setLocale = (locale: string) => {
    store.commit('reservation/setReservationLocale', locale);
  };

  const postReservation = async (status: string) => {
    if (window.dataLayer) {
      window.dataLayer.push({ event: 'submit_booking' });
    }
    // Set submitted flag BEFORE making API calls to prevent watchers from triggering PUT requests
    reservationSubmitted.value = true;
    store.commit('reservation/setReservationSubmitting', true);
    await store.dispatch('reservation/setBookingStatus', status);
    store.commit('reservation/setReservationSubmitting', false);
  };

  const bookingId = computed(() => store.state.reservation.bookingId);

  const postBooking = async () => {
    try {
      const booking = (await store.dispatch(
        'reservation/postBooking',
      )) as BookingRead;
      updateReservationFromBooking(booking);
    } catch (error) {
      console.error('Failed to post booking:', error);
    }
  };

  const updateBooking = async () => {
    if (!(reservation.value as any).emailaddress) {
      return;
    }
    if (bookingId.value === undefined || bookingId.value === null) {
      await postBooking();
    } else {
      await putBooking();
    }
  };

  const earliestDate = computed<Date | undefined>(() => {
    const reservation = store.state.reservation.reservation;
    return (
      (reservation.rooms ?? []) // get dates from rooms
        .filter((room) => room.startDate)
        .map((room) => parseISO(room.startDate!))
        // combine with MICE days
        .concat(
          // get dates from days
          ...(reservation.days ?? [])
            .filter((day) => day.date!)
            .map((day) => parseISO(day.date!)),
        )
        // reduce to earliest
        .reduce((a: undefined | Date, b) => {
          if (a && b) {
            return a < b ? a : b;
          }
          return a ?? b;
        }, undefined)
    );
  });

  const bookingTransitions = computed(
    () => store.state.reservation.transitions,
  );

  return {
    numRooms,
    reservation,
    reservationLoaded,
    reservationChanged,
    addDay,
    deleteDay,
    setLocale,
    getExistingReservation,
    postReservation,
    reservationLoading,
    reservationSubmitting,
    parseURLParams,
    websiteURL,
    cloneDay,
    cloneRoom,
    missingInfo,
    breakfastSelectable,
    breakfastDefault,
    termsUrl,
    earliestDate,
    reservationLocation,
    updateBooking,
    bookingTransitions,
    getBooking,
    getBookingBilling,
    bookingId,
    bookingSubtotals,
    totalPriceIncludingTaxes,
    totalPriceExcludingTaxes,
    pricingTaxScheme,
    reservationSubmitted,
    bookingRateProducts,
    transitionBlockerList,
    fetchingTransitions,
  };
}
